"use client"

import { useMemo, useState } from "react";
import { Clipboard } from "lucide-react";

import { Input } from "@/components/ui/input";
import { copyToClipboard } from "@/lib/utils";
import { useTranslations } from "@/components/i18n/use-translations";

type Family = 4 | 6;

const parseIp = (raw: string): { family: Family; value: bigint } | null => {
    const value = raw.trim();

    const v4 = value.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (v4) {
        const octets = v4.slice(1).map(Number);
        if (octets.some((octet) => octet > 255)) {
            return null;
        }
        return { family: 4, value: octets.reduce((acc, octet) => (acc << BigInt(8)) + BigInt(octet), BigInt(0)) };
    }

    if (!/^[0-9a-fA-F:]+$/.test(value) || (value.match(/::/g) || []).length > 1) {
        return null;
    }
    const [left, right] = value.includes("::") ? value.split("::") : [value, null];
    const leftParts = left ? left.split(":") : [];
    const rightParts = right ? right.split(":") : [];
    const missing = 8 - leftParts.length - rightParts.length;
    if ((right === null && missing !== 0) || (right !== null && missing < 1)) {
        return null;
    }
    const groups = [...leftParts, ...Array(right === null ? 0 : missing).fill("0"), ...rightParts];
    if (groups.some((group) => !/^[0-9a-fA-F]{1,4}$/.test(group))) {
        return null;
    }
    return { family: 6, value: groups.reduce((acc, group) => (acc << BigInt(16)) + BigInt(parseInt(group, 16)), BigInt(0)) };
};

const formatIp = (value: bigint, family: Family) => {
    if (family === 4) {
        return [24, 16, 8, 0].map((shift) => Number((value >> BigInt(shift)) & BigInt(255))).join(".");
    }
    const groups = Array.from({ length: 8 }, (_, index) => Number((value >> BigInt((7 - index) * 16)) & BigInt(0xffff)).toString(16));
    // Compress the longest run of zero groups (RFC 5952)
    let bestStart = -1;
    let bestLength = 0;
    for (let index = 0; index < 8; ) {
        if (groups[index] !== "0") {
            index++;
            continue;
        }
        let end = index;
        while (end < 8 && groups[end] === "0") {
            end++;
        }
        if (end - index > bestLength && end - index > 1) {
            bestStart = index;
            bestLength = end - index;
        }
        index = end;
    }
    if (bestStart === -1) {
        return groups.join(":");
    }
    return `${groups.slice(0, bestStart).join(":")}::${groups.slice(bestStart + bestLength).join(":")}`;
};

const calculate = (raw: string) => {
    const [address, prefixRaw] = raw.trim().split("/");
    const ip = parseIp(address ?? "");
    if (!ip) {
        return null;
    }
    const bits = ip.family === 4 ? 32 : 128;
    const prefix = prefixRaw === undefined ? bits : Number(prefixRaw);
    if (!Number.isInteger(prefix) || prefix < 0 || prefix > bits) {
        return null;
    }

    const all = (BigInt(1) << BigInt(bits)) - BigInt(1);
    const hostMask = (BigInt(1) << BigInt(bits - prefix)) - BigInt(1);
    const netmask = all ^ hostMask;
    const network = ip.value & netmask;
    const broadcast = network | hostMask;
    const total = hostMask + BigInt(1);
    const hasHostRange = ip.family === 4 && prefix < 31;

    return {
        family: ip.family,
        prefix,
        network,
        broadcast,
        cidr: `${formatIp(network, ip.family)}/${prefix}`,
        netmask: formatIp(netmask, ip.family),
        wildcard: formatIp(hostMask, ip.family),
        firstHost: formatIp(hasHostRange ? network + BigInt(1) : network, ip.family),
        lastHost: formatIp(hasHostRange ? broadcast - BigInt(1) : broadcast, ip.family),
        broadcastText: formatIp(broadcast, ip.family),
        total: total.toLocaleString(),
        usable: (hasHostRange ? total - BigInt(2) : total).toLocaleString(),
    };
};

export function CidrCalculator() {
    const [cidr, setCidr] = useState("");
    const [probe, setProbe] = useState("");
    const { messages } = useTranslations();
    const t = messages.tools.cidrCalculator;

    const result = useMemo(() => calculate(cidr), [cidr]);
    const probeResult = useMemo(() => {
        const ip = parseIp(probe);
        if (!result || !ip || ip.family !== result.family) {
            return null;
        }
        return ip.value >= result.network && ip.value <= result.broadcast;
    }, [probe, result]);

    const rows = result
        ? [
              { label: t.network, value: result.cidr },
              { label: t.netmask, value: result.netmask },
              { label: t.wildcard, value: result.wildcard },
              ...(result.family === 4 ? [{ label: t.broadcast, value: result.broadcastText }] : []),
              { label: t.firstHost, value: result.firstHost },
              { label: t.lastHost, value: result.lastHost },
              { label: t.totalAddresses, value: result.total },
              { label: t.usableHosts, value: result.usable },
          ]
        : [];

    return (
        <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
            <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{t.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{t.title}</h1>
                    <p className="text-sm text-gray-400">{t.description}</p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input
                        value={cidr}
                        onChange={(event) => setCidr(event.target.value)}
                        placeholder={t.placeholder}
                        aria-label={t.label}
                        spellCheck={false}
                        className="h-11 border border-slate-800 bg-transparent font-mono text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                    />
                    <Input
                        value={probe}
                        onChange={(event) => setProbe(event.target.value)}
                        placeholder={t.checkPlaceholder}
                        spellCheck={false}
                        aria-label={t.checkLabel}
                        className="h-11 border border-slate-800 bg-transparent font-mono text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                    />
                </div>
                {probeResult !== null && (
                    <p
                        className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                            probeResult ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-amber-500/40 bg-amber-500/10 text-amber-100"
                        }`}
                    >
                        {probeResult ? t.inRange : t.outOfRange}
                    </p>
                )}
            </div>

            {cidr.trim() && !result && <p className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{t.invalid}</p>}

            {result && (
                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {rows.map((row) => (
                        <div key={row.label} className="flex items-center justify-between gap-3 rounded-xl border border-slate-800/80 bg-slate-950/60 px-4 py-3">
                            <div className="min-w-0">
                                <dt className="text-xs uppercase tracking-wider text-gray-400">{row.label}</dt>
                                <dd className="break-all font-mono text-sm text-cyan-100">{row.value}</dd>
                            </div>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(row.value)}
                                className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                                aria-label={`${t.copy} ${row.label}`}
                            >
                                <Clipboard className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </dl>
            )}
        </div>
    );
}
